/**
 * Easing for the print-process films. Everything is a pure function of the
 * film's progress, so any frame of a loop can be drawn on its own.
 *
 * The machine moves with pneumatic grammar: a valve pause, a hard
 * trapezoidal stroke that arrives slightly past its stop and cushions back.
 * Only the silicone pad is soft (squash, roll, wobble).
 */

export const clamp01 = (v) => Math.min(1, Math.max(0, v))

/** Local 0..1 progress of p through the window [a, b]. */
export const seg = (p, a, b) => clamp01((p - a) / (b - a))

export const lerp = (a, b, k) => a + (b - a) * k

export const smoothstep = (k) => k * k * (3 - 2 * k)

export const easeInOutCubic = (k) => (k < 0.5 ? 4 * k ** 3 : 1 - (-2 * k + 2) ** 3 / 2)

export const easeOutCubic = (k) => 1 - (1 - k) ** 3

export const easeInQuad = (k) => k * k

export const easeOutExpo = (k) => (k >= 1 ? 1 : 1 - 2 ** (-10 * k))

const VALVE = 0.06
const ARRIVE = 0.92
const ACCEL = 0.22
const DECEL = 0.1

/**
 * Pneumatic stroke: 6 % valve pause, trapezoidal velocity (22 % accel, 10 %
 * hard decel), arrives at 1 + over at k 0.92, cushions back to 1.
 */
export function pneu(k, over = 0.015) {
  if (k <= VALVE) return 0
  if (k >= ARRIVE) return 1 + over * (1 - smoothstep((k - ARRIVE) / (1 - ARRIVE)))
  const u = (k - VALVE) / (ARRIVE - VALVE)
  const v = 1 / (1 - ACCEL / 2 - DECEL / 2)
  let s
  if (u < ACCEL) s = (v * u * u) / (2 * ACCEL)
  else if (u <= 1 - DECEL) s = v * (u - ACCEL / 2)
  else s = 1 - (v * (1 - u) ** 2) / (2 * DECEL)
  return s * (1 + over)
}

/**
 * Retraction that departs at speed (no valve pause): a long acceleration,
 * so the first part of the stroke — the pad peeling off its surface — is
 * slow and the rest is the cylinder's lift, then a hard stop into a 2 %
 * cushion. One curve, so the velocity never stops between peel and lift.
 */
export function retract(k, over = 0.013) {
  if (k <= 0) return 0
  if (k >= ARRIVE) return 1 + over * (1 - smoothstep((k - ARRIVE) / (1 - ARRIVE)))
  const u = k / ARRIVE
  const a = 0.5
  const d = 0.12
  const v = 1 / (1 - a / 2 - d / 2)
  let s
  if (u < a) s = (v * u * u) / (2 * a)
  else if (u <= 1 - d) s = v * (u - a / 2)
  else s = 1 - (v * (1 - u) ** 2) / (2 * d)
  return s * (1 + over)
}

/** Approach into contact: valve pause, accelerate, arrive AT SPEED. */
export function approach(k) {
  if (k <= VALVE) return 0
  const u = Math.min(1, (k - VALVE) / (1 - VALVE))
  const a = 0.35
  return u < a ? (u * u) / (2 * a * (1 - a / 2)) : (u - a / 2) / (1 - a / 2)
}

/** Damped settle after a hard stop, in degrees (t in 0..1). */
export const wobble = (t) => (t <= 0 || t >= 1 ? 0 : 4 * Math.exp(-5 * t) * Math.sin(4 * Math.PI * t))

/** Geometric scale interpolation, so a push-in feels even. */
export const zoom = (s0, s1, e) => s0 * (s1 / s0) ** e
